import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";

const MIN_DEPOSIT = 5;

const Deposit = ({ user }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("amount");
  const [isChecking, setIsChecking] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [depositAddress, setDepositAddress] = useState("");

  const userId = user?.uid || null;
  const API_URL = process.env.REACT_APP_API_URL;
  const defaultPaymentMethodId =
    process.env.REACT_APP_CRYPTRUM_DEPOSIT_PAYMENT_METHOD_ID || "7";
  const numericAmount = Number(amount || 0);
  const hasValidAmount = numericAmount >= MIN_DEPOSIT;

  const selectedPaymentMethod =
    paymentMethods.find(
      (method) => String(method.id) === String(selectedPaymentMethodId),
    ) ||
    (String(selectedPaymentMethodId) === String(defaultPaymentMethodId)
      ? {
          id: defaultPaymentMethodId,
          name: "USDT",
          network_name: "TRC20",
        }
      : null);

  const qrValue = useMemo(() => {
    if (!depositAddress || !hasValidAmount) return "";
    return depositAddress;
  }, [depositAddress, hasValidAmount]);

  useEffect(() => {
    if (!API_URL) {
      setErrorMessage("Backend API URL is not configured.");
      return undefined;
    }

    let cancelled = false;

    async function loadPaymentMethods() {
      try {
        const res = await fetch(
          `${API_URL}/api/cryptrum/payment-methods?type=deposit`,
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.error || "Failed to load payment methods.");

        const methods = data?.data?.data || data?.data || [];
        const activeMethods = methods.filter(
          (method) =>
            Number(method.deposit_status) === 1 &&
            String(method.name).toUpperCase() === "USDT",
        );
        if (cancelled) return;

        setPaymentMethods(activeMethods);
        const preferred =
          activeMethods.find(
            (method) => String(method.id) === String(defaultPaymentMethodId),
          ) ||
          activeMethods.find(
            (method) =>
              method.name === "USDT" && method.network_name === "TRC20",
          ) ||
          activeMethods.find((method) => method.name === "USDT") ||
          activeMethods[0];

        if (preferred) {
          setSelectedPaymentMethodId(String(preferred.id));
        } else if (defaultPaymentMethodId) {
          setSelectedPaymentMethodId(String(defaultPaymentMethodId));
        }
      } catch (err) {
        if (!cancelled) {
          if (defaultPaymentMethodId) {
            setSelectedPaymentMethodId(String(defaultPaymentMethodId));
            return;
          }
          setErrorMessage(
            err?.message || "Could not load gateway payment methods.",
          );
        }
      }
    }

    loadPaymentMethods();

    return () => {
      cancelled = true;
    };
  }, [API_URL, defaultPaymentMethodId]);

  const handleCopy = () => {
    if (!depositAddress) return;
    navigator.clipboard.writeText(depositAddress);
    toast.success("Deposit address copied.");
  };

  const handlePaymentSuccess = (message) => {
    setSuccessMessage(message);
    toast.success(message);
    setTimeout(() => navigate("/"), 1400);
  };

  const handleAmountChange = (event) => {
    setAmount(event.target.value);
    setDepositAddress("");
    setSuccessMessage("");
    setErrorMessage("");
    if (step !== "amount") setStep("amount");
  };

  const handleNext = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!API_URL) return setErrorMessage("Backend API URL is not configured.");
    if (!userId) return setErrorMessage("Please log in before depositing.");
    if (!hasValidAmount)
      return setErrorMessage(`Minimum deposit is ${MIN_DEPOSIT} USDT.`);
    if (!selectedPaymentMethodId) {
      return setErrorMessage(
        "Deposit network is not ready yet. Please try again.",
      );
    }

    setIsLoadingAddress(true);
    try {
      const res = await fetch(`${API_URL}/api/cryptrum/deposit-address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          paymentMethodId: Number(selectedPaymentMethodId),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.error ||
            "Could not create deposit address. Please contact support to enable this network.",
        );
      }
      setDepositAddress(data.address);
      setStep("payment");
    } catch (err) {
      setDepositAddress("");
      setErrorMessage(err?.message || "Could not create deposit address.");
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleBack = () => {
    setStep("amount");
    setDepositAddress("");
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleCheckPayment = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!API_URL) return setErrorMessage("Backend API URL is not configured.");
    if (!userId) return setErrorMessage("Please log in before depositing.");
    if (!hasValidAmount)
      return setErrorMessage(`Minimum deposit is ${MIN_DEPOSIT} USDT.`);
    if (!selectedPaymentMethodId || !depositAddress) {
      return setErrorMessage("Deposit address is not ready yet.");
    }

    setIsChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/cryptrum/sync-deposits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          paymentMethodId: Number(selectedPaymentMethodId),
        }),
      });

      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || "Could not check payment yet.");

      const creditedAmount = (data.credited || []).reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0,
      );

      if (creditedAmount > 0) {
        handlePaymentSuccess(
          `Deposit successful: ${creditedAmount.toFixed(2)} USDT credited.`,
        );
        return;
      }

      setSuccessMessage("");
      setErrorMessage(
        "Payment is not confirmed yet. Please wait a moment and check again.",
      );
    } catch (err) {
      setErrorMessage(err?.message || "Could not check payment yet.");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-gray-900 px-4 pt-20 pb-10">
      <div className="w-full max-w-md space-y-5 rounded-xl bg-slate-800 p-6 text-white shadow-lg">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Deposit</h2>
          <div className="mt-3 inline-flex rounded-full bg-slate-900/70 p-1 text-xs font-bold">
            <span
              className={`rounded-full px-3 py-1 ${
                step === "amount"
                  ? "bg-lime-400 text-slate-950"
                  : "text-slate-300"
              }`}
            >
              1 Amount
            </span>
            <span
              className={`rounded-full px-3 py-1 ${
                step === "payment"
                  ? "bg-lime-400 text-slate-950"
                  : "text-slate-300"
              }`}
            >
              2 Pay
            </span>
          </div>
        </div>

        {step === "amount" ? (
          <>
            <div>
              <label className="mb-2 block font-bold text-lime-300">
                Deposit Amount (USDT)
              </label>
              <input
                type="number"
                value={amount}
                onChange={handleAmountChange}
                min={MIN_DEPOSIT}
                step="0.01"
                className="w-full rounded-2xl border-2 border-lime-400 bg-slate-900 px-5 py-3 text-2xl font-bold text-lime-200 shadow-md outline-none"
                placeholder="Enter amount"
              />
              <div className="mt-2 text-xs text-slate-400">
                Minimum deposit: {MIN_DEPOSIT} USDT
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-900/70 p-4 text-sm">
              <div>
                <div className="text-slate-400">Currency</div>
                <div className="font-bold text-green-400">
                  {selectedPaymentMethod?.name || "USDT"}
                </div>
              </div>
              <div>
                <div className="text-slate-400">Network</div>
                <div className="font-bold text-green-400">
                  {selectedPaymentMethod?.network_name || "TRC20"}
                </div>
              </div>
            </div>

            {paymentMethods.length > 1 ? (
              <div>
                <label className="mb-2 block font-bold text-lime-300">
                  Network
                </label>
                <select
                  value={selectedPaymentMethodId}
                  onChange={(event) =>
                    setSelectedPaymentMethodId(event.target.value)
                  }
                  className="w-full rounded-xl border-2 border-lime-400 bg-slate-900 px-4 py-3 text-lime-100 transition focus:border-lime-300"
                >
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name} {method.network_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleNext}
              disabled={
                isLoadingAddress || !hasValidAmount || !selectedPaymentMethodId
              }
              className="w-full rounded-xl bg-green-500 py-3 font-black text-slate-950 transition hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoadingAddress ? "Creating Payment..." : "Next"}
            </button>
          </>
        ) : null}

        {step === "payment" ? (
          <>
            <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-center text-xs font-semibold text-yellow-100">
              Send only {selectedPaymentMethod?.name || "USDT"} on{" "}
              {selectedPaymentMethod?.network_name || "TRC20"}. Other tokens or
              networks may be lost.
            </div>

            <div className="flex justify-center">
              <div className="flex min-h-[186px] min-w-[186px] items-center justify-center rounded-lg border bg-white p-2">
                {qrValue ? (
                  <QRCodeCanvas value={qrValue} size={170} />
                ) : (
                  <span className="px-3 text-center text-xs text-slate-500">
                    Address unavailable
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-slate-900/70 p-4 text-center text-sm">
              <div className="text-xs text-slate-400">Pay exactly</div>
              <div className="text-2xl font-black text-lime-300">
                {numericAmount.toFixed(2)} USDT
              </div>
              <div className="mt-2 text-slate-300">
                {selectedPaymentMethod?.name || "USDT"}{" "}
                {selectedPaymentMethod?.network_name || "TRC20"}
              </div>
              <div className="mt-3 break-all text-xs text-green-400">
                {depositAddress}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="rounded-xl bg-slate-700 py-3 font-bold text-white transition hover:bg-slate-600"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-xl bg-gray-700 py-3 font-bold text-white transition hover:bg-gray-600"
              >
                Copy Address
              </button>
            </div>

            <button
              type="button"
              onClick={handleCheckPayment}
              disabled={isChecking || !depositAddress}
              className="w-full rounded-xl bg-green-500 py-3 font-black text-slate-950 transition hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isChecking ? "Checking Payment..." : "I Have Paid"}
            </button>
          </>
        ) : null}

        {successMessage ? (
          <p className="text-sm font-medium text-green-400">{successMessage}</p>
        ) : null}
        {errorMessage ? (
          <p className="text-sm font-medium text-red-400">{errorMessage}</p>
        ) : null}
      </div>
    </div>
  );
};

export default Deposit;
